import {
  Box,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useColorModeValue,
  Text as ChakraText,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FaArrowLeft, FaEye } from "react-icons/fa";

// Custom AG Grid styles (unchanged)
const agGridStyles = `
  .custom-ag-grid.ag-theme-alpine .ag-root-wrapper {
    border-radius: 12px !important;
    overflow: hidden !important;
    border: 1px solid #e2e8f0 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header {
    background-color: #1a365d !important;
    border-top-left-radius: 12px !important;
    border-top-right-radius: 12px !important;
    height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-row {
    height: 70px !important;
    min-height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    color: #ffffff !important;
    padding: 0 16px !important;
    border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell:last-child {
    border-right: none !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell.ag-numeric-cell {
    justify-content: center !important;
    text-align: center !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row {
    height: 64px !important;
    min-height: 64px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    width: 100% !important;
    text-align: left !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-even {
    background-color: #f8fafc !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-odd {
    background-color: #ffffff !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    height: 64px !important;
    padding: 0 8px !important;
    font-size: 16px !important;
    color: #1a202c !important;
    font-weight: 500 !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: break-word !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-panel {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding: 12px !important;
    background-color: #f7fafc !important;
    border-top: 1px solid #e2e8f0 !important;
  }
`;

export default function VisibilityItemsView() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [imageUrls, setImageUrls] = useState({
    actualImage: undefined,
    annotatedImage: undefined,
  });
  const gridRef = useRef(null);

  // Fetch table data
  useEffect(() => {
    const fetchTableData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VISIBILITY_ITEMS}`;
        const response = await apiClient.get(url);
        const apiData = Array.isArray(response.data)
          ? response.data
          : [response.data];
        const transformedData = apiData.map((item, index) => ({
          rowIndex: index,
          storeName: item.store?.storeName?.toString() || "N/A",
          visibilityItem: item.product?.productName?.toString() || "N/A",
          details: item.value?.toString() || "0.0",
          fileName: item.fileName || "N/A",
          s3pathActualFile: item.s3pathActualFile || null,
          s3pathAnnotatedFile: item.s3pathAnnotatedFile || null,
        }));
        setTableData(transformedData);
      } catch (err) {
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };
    fetchTableData();
  }, []);

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleEyeClick = async (rowData) => {
    setSelectedRow(rowData);
    setIsModalOpen(true);
    setImageUrls({ actualImage: undefined, annotatedImage: undefined });
  
    try {
      const bucketName = "imageinsightimagesbeverages";
  
      // helper: get presigned URL directly like StoreReportDashboard
      const fetchPresignedUrl = async (s3Path) => {
        if (!s3Path) return null;
        const { data: presignedUrl } = await apiClient.get(
          `${API_CONFIG.ENDPOINTS.STORE_REPORT.S3_PRESIGNED_URL}?fileName=${encodeURIComponent(
            s3Path
          )}&bucketName=${bucketName}&operation=download`
        );
        return presignedUrl; // use it directly
      };
  
      const [actualImageUrl, annotatedImageUrl] = await Promise.all([
        fetchPresignedUrl(rowData.s3pathActualFile),
        fetchPresignedUrl(rowData.s3pathAnnotatedFile),
      ]);
  
      setImageUrls({
        actualImage: actualImageUrl,
        annotatedImage: annotatedImageUrl,
      });
    } catch (err) {
      console.error("Error fetching images:", err);
      setImageUrls({
        actualImage: null,
        annotatedImage: null,
      });
    }
  };
  

  const columnDefs = useMemo(
    () => [
      {
        headerName: "FileName",
        field: "fileName",
        // cellClass: "ag-numeric-cell",
        sortable: true,
        filter: true,
        width: 200,
        cellRenderer: (params) => (
          <Flex alignItems="center" gap="2">
            <ChakraText>{params.value || "N/A"}</ChakraText>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEyeClick(params.data)}
            >
              <FaEye />
            </Button>
          </Flex>
        ),
      },
      {
        headerName: "StoreName",
        field: "storeName",
        sortable: true,
        filter: true,
        width: 100,
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "VisibilityItem",
        field: "visibilityItem",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Details",
        field: "details",
        sortable: true,
        filter: true,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      cellStyle: { textAlign: "left" },
    }),
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <style>{agGridStyles}</style>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
        </Flex>
        <Box
          className="custom-ag-grid ag-theme-alpine"
          height="650px"
          width="100%"
          overflow="hidden"
        >
          <AgGridReact
            ref={gridRef}
            rowData={tableData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={64}
            headerHeight={70}
            pagination={true}
            paginationPageSize={8}
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            loadingOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="red.500">Loading...</ChakraText>
              </Flex>
            )}
            noRowsOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="gray.500">
                  {error || "No data available"}
                </ChakraText>
              </Flex>
            )}
            onGridReady={(params) => {
              if (loading) params.api.showLoadingOverlay();
              else if (tableData.length === 0 || error)
                params.api.showNoRowsOverlay();
              else params.api.hideOverlay();
            }}
          />
        </Box>
      </Card>
      <Modal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  size="4xl" // centered large modal
>
  <ModalOverlay />
  <ModalContent borderRadius="md">
    <ModalHeader>
      Image Details for {selectedRow?.fileName || "N/A"}
    </ModalHeader>
    <ModalCloseButton />
    <ModalBody>
      {selectedRow && (
        <Box mb="20px">
          <Flex justify="space-between" align="center">
            <ChakraText fontSize="lg" fontWeight="bold">
              Store Name: {selectedRow.storeName || "N/A"}
            </ChakraText>
            <ChakraText fontSize="lg" fontWeight="bold">
              Visibility Item: {selectedRow.visibilityItem || "N/A"}
            </ChakraText>
          </Flex>
        </Box>
      )}
      <Flex
        direction="row"
        gap="30px"
        justify="center"
        alignItems="center"
        flexWrap="wrap"
      >
        {/* Actual Image */}
        <Flex direction="column" alignItems="center" gap="10px">
          <ChakraText fontSize="lg" fontWeight="bold">
            Actual Image
          </ChakraText>
          <img
            src={
              imageUrls.actualImage ||
              "https://via.placeholder.com/400x400?text=No+Actual+Image"
            }
            alt="Actual"
            style={{
              maxWidth: "400px",
              maxHeight: "600px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </Flex>

        {/* Annotated Image */}
        <Flex direction="column" alignItems="center" gap="10px">
          <ChakraText fontSize="lg" fontWeight="bold">
            Annotated Image
          </ChakraText>
          <img
            src={
              imageUrls.annotatedImage ||
              "https://via.placeholder.com/400x400?text=No+Annotated+Image"
            }
            alt="Annotated"
            style={{
              maxWidth: "400px",
              maxHeight: "600px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </Flex>
      </Flex>
    </ModalBody>
  </ModalContent>
</Modal>

    </Flex>
  );
}